import React from 'react';
import { ToolComponentProps } from './types';
import { Plus, Shuffle } from 'lucide-react';

interface AddRandomInput {
  num?: number;
}

export const AddRandomTool: React.FC<ToolComponentProps> = ({ 
  toolName, 
  input, 
  output, 
  isError,
  isExpanded,
  onToggle
}) => {
  const typedInput = input as AddRandomInput;
  const inputNum = typedInput?.num || 0;
  
  // Parse output to extract the random number and result
  const parseOutput = (output: string): { randomAdded: number, result: number } | null => {
    if (!output || typeof output !== 'string') return null;
    
    // Match pattern like "Randomly added: 6, for a final result of: 48"
    const match = output.match(/Randomly added: (\d+), for a final result of: (\d+)/);
    if (match) {
      return {
        randomAdded: parseInt(match[1]),
        result: parseInt(match[2])
      };
    }
    return null;
  };
  
  const parsed = output && !isError ? parseOutput(output) : null;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Shuffle className="h-4 w-4 text-primary" />
        <span className="font-semibold">Add Random</span>
      </div>

      {parsed && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center justify-center gap-3">
            <div className="text-center">
              <div className="text-muted-foreground mb-1">Input</div>
              <div className="font-bold">{inputNum}</div>
            </div>

            <Plus className="h-5 w-5 text-primary" />

            <div className="text-center">
              <div className="text-muted-foreground mb-1">Random</div>
              <div className="font-bold text-primary">{parsed.randomAdded}</div>
            </div>

            <div className="text-muted-foreground">=</div>

            <div className="text-center">
              <div className="text-muted-foreground mb-1">Result</div>
              <div className="font-bold text-primary">
                {parsed.result}
              </div>
            </div>
          </div>
        </div>
      )}

      {output && !parsed && !isError && (
        <div className="bg-background/50 rounded-lg p-3">
          <div>{output}</div>
        </div>
      )}

      {output && isError && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="text-destructive">
            Error: {output}
          </div>
        </div>
      )}

      {!output && input && (
        <div className="text-muted-foreground">
          Adding random number to {inputNum}...
        </div>
      )}
    </div>
  );
};